/**
 * Sub-components for RmcOrderList
 * Breaking down the main component into smaller, reusable pieces
 */

import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {computeSplit} from '@hconnect/tuc-processes/fields/loadBalance/loadBalance.utils'
import {formatQuantityDisplay} from '../../../shared/order-editor/utils/quantity.utils'
import {
  AccessTime,
  ArrowForward,
  Delete,
  Info,
  InfoOutlined,
  NotInterested,
  StickyNote2
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material'
import {TFunction} from 'i18next'
import React from 'react'

import {formattedDate} from '../../../../../Pages/Order/OrderDetails/components/utils'
import {useFeatureSlotManagementForRmcOrderIntakeEnabled} from '../../../shared/order-editor/components/fields/Time/useFeatureFlag'
import {getUnloadingMethodText} from '../../../shared/order-editor/components/fields/UnloadingMethod/unloadingMethodUtils'
import {useRegionFeatures} from '../../../shared/order-editor/hooks/useRegionConfig'
import {RmcOrder} from '../../types/order'
import {getMaterialVariationsDisplay} from '../../utils/orderUtils'
import {getRequestedTimeColor} from '../../utils/requestedTimeColor'
import {InfoPopover} from '../shared/InfoPopover'

import {
  getCardContentStyles,
  getOrderCardStyles,
  getPlaceOrderButtonStyles,
  getTableRowStyles,
  getTableStyles
} from './RmcOrderList.styles'

/**
 * Helper function to get display time from order data
 */
export const getDisplayTime = (
  order: RmcOrder,
  t: (key: string, defaultValue?: string) => string,
  language: string
): string => {
  if (order.formData.dateTime && order.formData.dateTime[0]) {
    const [, timeOnly] = formattedDate(order.formData.dateTime[0], language)
    if (timeOnly) {
      return timeOnly
    }
  }

  // Default fallback
  return t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')
}

/**
 * Helper function to get interval display information for order list
 */
export const getIntervalDisplayInfo = (
  order: RmcOrder,
  t: (key: string, defaultValue?: string) => string
): Array<{value: string; label: string}> => {
  const intervals: Array<{value: string; label: string}> = []

  // Add spacing if present
  if (order.formData.spacingOrFlowRate?.type === 'loadSpace') {
    const value = `${order.formData.spacingOrFlowRate.loadSpace} ${order.formData.spacingOrFlowRate.loadSpaceUom}`
    const label = t('orderIntake.rmc.orderManagement.loadSpacing', 'Load spacing')
    intervals.push({value, label})
  }

  // Add flow rate if present
  if (order.formData.spacingOrFlowRate?.type === 'flowRate') {
    const value = `${order.formData.spacingOrFlowRate.flowRate} ${order.formData.spacingOrFlowRate.flowRateUom}`
    const label = t('orderIntake.rmc.orderManagement.flowRate', 'Flow rate')
    intervals.push({value, label})
  }

  // Add time on job site if present
  if (order.formData.timeOnJobSite) {
    const value = `${order.formData.timeOnJobSite.time} ${order.formData.timeOnJobSite.timeUom}`
    const label = t('orderIntake.rmc.orderManagement.timeOnJobSite', 'Time on job site')
    intervals.push({value, label})
  }

  return intervals
}

/**
 * Order table view component for desktop
 */
export const OrderTableView: React.FC<{
  orders: RmcOrder[]
  selectedOrderId: string | null
  onSelectOrder: (orderId: string | null) => void
  onDeleteOrder: (orderId: string) => void
  t: TFunction
  language: string
}> = ({orders, selectedOrderId, onSelectOrder, onDeleteOrder, t, language}) => {
  const theme = useTheme()
  const textPrimary = theme.palette.text.primary
  const textSecondary = theme.palette.text.secondary
  const errorColor = theme.palette.error.main
  const slotManagementEnabled = useFeatureSlotManagementForRmcOrderIntakeEnabled()

  // Get region features to check if Usage step is enabled
  const regionFeatures = useRegionFeatures()
  const isUsageEnabled = regionFeatures.hasConcreteUsage
  const isUnloadingMethodEnabled = regionFeatures.hasUnloadingMethod
  const {step: quantityStep} = useFieldConfigurationContext('quantity')

  // State for popover
  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLElement | SVGElement | null>(
    null
  )
  const [popoverContent, setPopoverContent] = React.useState<string>('')

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement | SVGElement>,
    content: string
  ) => {
    setPopoverAnchorEl(event.currentTarget)
    setPopoverContent(content)
  }

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null)
    setPopoverContent('')
  }

  return (
    <Box sx={{overflow: 'auto', width: '100%'}}>
      <Table sx={getTableStyles()}>
        <TableHead>
          <TableRow>
            <TableCell sx={{width: '12%'}}>
              {t('orderIntake.rmc.orderManagement.tableHeaders.date')}
            </TableCell>
            <TableCell sx={{width: '18%'}}>
              {t('orderIntake.rmc.orderManagement.tableHeaders.mix')}
            </TableCell>
            <TableCell sx={{width: '10%'}}>
              {t('orderIntake.rmc.orderManagement.tableHeaders.quantity')}
            </TableCell>
            {isUsageEnabled && (
              <TableCell sx={{width: '8%'}}>
                {t('orderIntake.rmc.orderManagement.tableHeaders.usage')}
              </TableCell>
            )}
            {isUnloadingMethodEnabled && (
              <TableCell sx={{width: '12%'}}>
                {t('orderIntake.rmc.orderManagement.tableHeaders.unloading')}
              </TableCell>
            )}
            <TableCell sx={{width: '12%'}}>
              {t('orderIntake.rmc.orderManagement.tableHeaders.scheduling')}
            </TableCell>
            <TableCell sx={{width: '6%', textAlign: 'center'}}>
              {t('orderIntake.rmc.orderManagement.tableHeaders.note')}
            </TableCell>
            <TableCell sx={{width: '15%'}}>
              {t('orderIntake.rmc.orderManagement.tableHeaders.poNumber')}
            </TableCell>
            <TableCell sx={{width: '7%'}}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order, index) => {
            // Calculate material variations once per row for performance
            const materialVariations = getMaterialVariationsDisplay(order)
            const hasMaterialVariations = !!materialVariations
            const requestedTimeColor = getRequestedTimeColor(
              theme.palette,
              slotManagementEnabled,
              order.requiresDispatchApproval
            )

            return (
              <TableRow
                key={order.id}
                sx={getTableRowStyles({isSelected: selectedOrderId === order.id})}
                onClick={() => onSelectOrder(order.id)}
              >
                {/* Date Column */}
                <TableCell data-test-id={'order-date-' + index}>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                    <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                      {formattedDate(order.formData.dateTime?.[0], language)[0]}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                      <AccessTime
                        sx={{
                          fontSize: '12px',
                          color: requestedTimeColor
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: requestedTimeColor
                        }}
                      >
                        {getDisplayTime(order, t, language)}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* Mix Column */}
                <TableCell data-test-id={'order-mix-' + index}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px', flex: 1}}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '18px',
                          fontStyle: hasMaterialVariations ? 'italic' : 'normal',
                          color: textPrimary,
                          width: '128px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {order.formData.material?.materialDescription}
                        {hasMaterialVariations ? ' *' : ''}
                      </Typography>
                      {hasMaterialVariations && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: '16px',
                            color: textPrimary,
                            width: '128px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          * {materialVariations}
                        </Typography>
                      )}
                    </Box>
                    {hasMaterialVariations && (
                      <InfoOutlined
                        onClick={(e) => {
                          e.stopPropagation()
                          const materialName = order.formData.material?.materialDescription || ''
                          const formattedContent = t(
                            'orderIntake.rmc.orderManagement.changedMaterialVariationsMessage',
                            {
                              materialName,
                              materialVariations
                            }
                          )
                          handlePopoverOpen(e, formattedContent)
                        }}
                        sx={{
                          fontSize: '16px',
                          color: textSecondary,
                          cursor: 'pointer'
                        }}
                      />
                    )}
                  </Box>
                </TableCell>

                {/* Quantity Column */}
                <TableCell data-test-id={'order-quantity-' + index}>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                    {(() => {
                      const qty =
                        order.formData.quantity.type === 'volume'
                          ? order.formData.quantity.volume
                          : order.formData.quantity.minQuantity // TODO Check how should we handle fullLoads scenario for split load ?
                      const split = computeSplit(order.formData.maxLoadSize, qty, quantityStep)
                      return (
                        <>
                          <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                            {formatQuantityDisplay(order.formData.quantity, t)}
                            {split &&
                              order.formData.plusLoadExpected &&
                              ` + ${t('orderIntake.rmc.orderManagement.plusLoad')}`}
                          </Typography>
                          {!split && order.formData.plusLoadExpected && (
                            <Typography
                              sx={{fontSize: '12px', fontWeight: 500, color: textPrimary}}
                            >
                              {t('orderIntake.rmc.orderManagement.plusLoad')}
                            </Typography>
                          )}
                          {split && (
                            <Typography
                              sx={{fontSize: '12px', fontWeight: 500, color: textPrimary}}
                            >
                              {t(
                                'orderIntake.rmc.loadBalance.splitLabel',
                                '(split: {{first}}; {{second}})',
                                {
                                  first: split.firstTruck,
                                  second: split.secondTruck
                                }
                              )}
                            </Typography>
                          )}
                        </>
                      )
                    })()}
                  </Box>
                </TableCell>

                {/* Usage Column */}
                {isUsageEnabled && (
                  <TableCell data-test-id={'order-usage-' + index}>
                    <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                      {order.formData.concreteUsage || '-'}
                    </Typography>
                  </TableCell>
                )}

                {/* Unloading Column */}
                {isUnloadingMethodEnabled && (
                  <TableCell data-test-id={'order-unloading-' + index}>
                    <Typography
                      sx={{fontSize: '14px', fontWeight: 500, color: textPrimary, width: '86px'}}
                    >
                      {getUnloadingMethodText(
                        t,
                        order.formData.unloadingMethod?.unloadingMethodCode
                      )}
                    </Typography>
                  </TableCell>
                )}

                {/* Scheduling Column */}
                <TableCell data-test-id={'order-scheduling-' + index}>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                    {(() => {
                      const intervals = getIntervalDisplayInfo(order, t)

                      if (intervals.length === 0) {
                        return (
                          <Typography sx={{fontSize: '16px', fontWeight: 400, color: textPrimary}}>
                            {t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')}
                          </Typography>
                        )
                      }

                      return intervals.map((interval, idx) => (
                        <Box key={idx} sx={{display: 'flex', flexDirection: 'column', gap: '1px'}}>
                          <Typography sx={{fontSize: '16px', fontWeight: 400, color: textPrimary}}>
                            {interval.value}
                          </Typography>
                          <Typography sx={{fontSize: '12px', fontWeight: 500, color: textPrimary}}>
                            {interval.label}
                          </Typography>
                        </Box>
                      ))
                    })()}
                  </Box>
                </TableCell>

                {/* Note Column */}
                <TableCell sx={{textAlign: 'center'}} data-test-id={'order-note-' + index}>
                  {order.formData.driverInstructions || order.formData.additionalNotes ? (
                    <StickyNote2 sx={{fontSize: '14px', color: textSecondary}} />
                  ) : (
                    <NotInterested sx={{fontSize: '14px', color: textSecondary, opacity: 0.5}} />
                  )}
                </TableCell>

                {/* PO Number Column */}
                <TableCell data-test-id={'order-po-number-' + index}>
                  <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                    #{order.formData.poNumber}
                  </Typography>
                </TableCell>

                {/* Actions Column */}
                <TableCell sx={{textAlign: 'center'}} data-test-id={'order-actions-' + index}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteOrder(order.id)
                    }}
                    sx={{padding: '4px'}}
                  >
                    <Delete sx={{fontSize: '16px', color: errorColor}} />
                  </IconButton>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {/* Popover for material variations */}
      <InfoPopover
        open={popoverAnchorEl !== null}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        title={t('orderIntake.rmc.orderManagement.changedMaterialVariations')}
        content={popoverContent}
        testId="material-variations-popover"
      />
    </Box>
  )
}

/**
 * Order card view component for mobile
 */
export const OrderCardView: React.FC<{
  orders: RmcOrder[]
  selectedOrderId: string | null
  onSelectOrder: (orderId: string | null) => void
  onDeleteOrder: (orderId: string) => void
  t: TFunction
  language: string
}> = ({orders, selectedOrderId, onSelectOrder, onDeleteOrder, t, language}) => {
  const theme = useTheme()
  const textPrimary = theme.palette.text.primary
  const textSecondary = theme.palette.text.secondary
  const errorColor = theme.palette.error.main
  const slotManagementEnabled = useFeatureSlotManagementForRmcOrderIntakeEnabled()

  // Get region features to check if Usage step is enabled
  const regionFeatures = useRegionFeatures()
  const isUsageEnabled = regionFeatures.hasConcreteUsage
  const isUnloadingMethodEnabled = regionFeatures.hasUnloadingMethod
  const {step: quantityStep} = useFieldConfigurationContext('quantity')

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
      {orders.map((order) => {
        // Calculate material variations once per card for performance
        const materialVariations = getMaterialVariationsDisplay(order)
        const hasMaterialVariations = !!materialVariations
        const requestedTimeColor = getRequestedTimeColor(
          theme.palette,
          slotManagementEnabled,
          order.requiresDispatchApproval
        )

        return (
          <Card
            key={order.id}
            sx={getOrderCardStyles({isSelected: selectedOrderId === order.id})}
            onClick={() => onSelectOrder(order.id)}
          >
            <Box sx={getCardContentStyles()}>
              {/* Date and Time */}
              <Box sx={{display: 'flex', justifyContent: 'space-between', marginBottom: '12px'}}>
                <Box sx={{display: 'flex', flexDirection: 'column'}}>
                  <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                    {formattedDate(order.formData.dateTime?.[0], language)[0]}
                  </Typography>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                    <AccessTime
                      sx={{
                        fontSize: '12px',
                        color: requestedTimeColor
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: requestedTimeColor
                      }}
                    >
                      {getDisplayTime(order, t, language)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteOrder(order.id)
                  }}
                  sx={{padding: '11px'}}
                >
                  <Delete sx={{fontSize: '24px', color: errorColor}} />
                </IconButton>
              </Box>

              <Divider sx={{margin: '4px 0 12px'}} />

              {/* Order Details */}
              <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                {/* Mix */}
                <Box
                  sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}
                >
                  <Typography sx={{fontSize: '14px', fontWeight: 400, color: textSecondary}}>
                    {t('orderIntake.rmc.orderManagement.tableHeaders.mix')}
                  </Typography>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '2px'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '18px',
                          fontStyle: hasMaterialVariations ? 'italic' : 'normal',
                          color: textPrimary,
                          maxWidth: '140px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {order.formData.material?.materialDescription}
                        {hasMaterialVariations ? ' *' : ''}
                      </Typography>
                      {hasMaterialVariations && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: '16px',
                            color: textPrimary,
                            maxWidth: '140px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          * {materialVariations}
                        </Typography>
                      )}
                    </Box>
                    <Info sx={{fontSize: '16px', color: textSecondary}} />
                  </Box>
                </Box>

                {/* Quantity */}
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography sx={{fontSize: '14px', fontWeight: 400, color: textSecondary}}>
                    {t('orderIntake.rmc.orderManagement.tableHeaders.quantity')}
                  </Typography>
                  <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                    <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                      {formatQuantityDisplay(order.formData.quantity, t)}
                    </Typography>
                    {order.formData.plusLoadExpected && (
                      <Typography sx={{fontSize: '12px', fontWeight: 500, color: textPrimary}}>
                        {t('orderIntake.rmc.orderManagement.plusLoad')}
                      </Typography>
                    )}
                    {(() => {
                      const split = computeSplit(
                        order.formData.maxLoadSize,
                        order.formData.quantity.type === 'volume'
                          ? order.formData.quantity.volume
                          : order.formData.quantity.minQuantity, // TODO Check how should we handle fullLoads scenario for split load ?
                        quantityStep
                      )
                      return split ? (
                        <Typography sx={{fontSize: '12px', fontWeight: 500, color: textPrimary}}>
                          {t(
                            'orderIntake.rmc.loadBalance.splitLabel',
                            '(split: {{first}}; {{second}})',
                            {
                              first: split.firstTruck,
                              second: split.secondTruck
                            }
                          )}
                        </Typography>
                      ) : null
                    })()}
                  </Box>
                </Box>

                {/* Usage */}
                {isUsageEnabled && (
                  <Box
                    sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                  >
                    <Typography sx={{fontSize: '14px', fontWeight: 400, color: textSecondary}}>
                      {t('orderIntake.rmc.orderManagement.tableHeaders.usage')}
                    </Typography>
                    <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                      {order.formData.concreteUsage || '-'}
                    </Typography>
                  </Box>
                )}

                {/* Unloading */}
                {isUnloadingMethodEnabled && (
                  <Box
                    sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                  >
                    <Typography sx={{fontSize: '14px', fontWeight: 400, color: textSecondary}}>
                      {t('orderIntake.rmc.orderManagement.tableHeaders.unloading')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: textPrimary,
                        maxWidth: '180px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {getUnloadingMethodText(
                        t,
                        order.formData.unloadingMethod?.unloadingMethodCode
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Scheduling */}
                <Box
                  sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}
                >
                  <Typography sx={{fontSize: '14px', fontWeight: 400, color: textSecondary}}>
                    {t('orderIntake.rmc.orderManagement.tableHeaders.scheduling')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: '4px'
                    }}
                  >
                    {(() => {
                      const intervals = getIntervalDisplayInfo(order, t)

                      if (intervals.length === 0) {
                        return (
                          <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                            {t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')}
                          </Typography>
                        )
                      }

                      return intervals.map((interval, idx) => (
                        <Box
                          key={idx}
                          sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}
                        >
                          <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                            {interval.value}
                          </Typography>
                          <Typography
                            sx={{fontSize: '12px', fontWeight: 400, color: textSecondary}}
                          >
                            {interval.label}
                          </Typography>
                        </Box>
                      ))
                    })()}
                  </Box>
                </Box>

                {/* PO Number */}
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography sx={{fontSize: '14px', fontWeight: 400, color: textSecondary}}>
                    {t('orderIntake.rmc.orderManagement.tableHeaders.poNumber')}
                  </Typography>
                  <Typography sx={{fontSize: '14px', fontWeight: 500, color: textPrimary}}>
                    #{order.formData.poNumber}
                  </Typography>
                </Box>

                {/* Notes Indicator */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  {order.formData.driverInstructions || order.formData.additionalNotes ? (
                    <>
                      <StickyNote2 sx={{fontSize: '14px', color: textSecondary}} />
                      <Typography sx={{fontSize: '14px', fontWeight: 400, color: textSecondary}}>
                        {t('orderIntake.rmc.orderManagement.hasNotes', 'Has notes')}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <NotInterested sx={{fontSize: '14px', color: textSecondary, opacity: 0.5}} />
                      <Typography
                        sx={{fontSize: '14px', fontWeight: 400, color: textSecondary, opacity: 0.5}}
                      >
                        {t('orderIntake.rmc.orderManagement.noNotes', 'No notes')}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Card>
        )
      })}
    </Box>
  )
}

/**
 * Place order button component for mobile only
 * TODO : unify with desktop version
 */
export const PlaceOrderButton: React.FC<{
  isPlacingOrders: boolean
  canPlaceOrders: boolean
  hasOrders: boolean
  onPlaceOrders: () => Promise<void>
  isMobile?: boolean
  t: (key: string, defaultValue?: string) => string
}> = ({isPlacingOrders, canPlaceOrders, hasOrders, onPlaceOrders, t}) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '24px'
      }}
    >
      <Button
        variant="contained"
        disabled={!canPlaceOrders || !hasOrders}
        onClick={onPlaceOrders}
        endIcon={isPlacingOrders ? null : <ArrowForward />}
        sx={getPlaceOrderButtonStyles()}
        data-test-id="place-order-button"
      >
        {isPlacingOrders ? (
          <CircularProgress size={24} sx={{color: theme.palette.common.white}} />
        ) : (
          t('orderIntake.rmc.orderManagement.placeOrder', 'Place Order')
        )}
      </Button>
    </Box>
  )
}
